import { analyzeComments } from '../commentAnalyzer';
import {
  saveCommentSignal,
  batchSaveSignals,
  getSignalsForVideo,
} from './commentSignalsService';
import {
  getVideoContext,
  analyzeAndUpdateVideoContext,
} from './videoContextService';
import {
  getChannelContext,
} from './channelContextService';
import {
  calculateConfidence,
  calculateVideoFrequency,
  calculateChannelFrequency,
  calculateEntityMatchBoost,
  calculateContradictionPenalty,
} from './confidenceService';
import { runPromotionProcess, shouldRunPromotion } from './promotionService';

/**
 * Analysis Integration Service
 * Integrates the learning system with comment analysis
 */

/**
 * Map analysis category to intent type
 * @param {string} category - Category from analyzeComments
 * @returns {string} Intent type
 */
function mapCategoryToIntent(category) {
  const mapping = {
    buyer: 'purchase',
    friction: 'friction',
    question: 'question',
    suggestion: 'suggestion',
    positive: 'feedback',
    other: 'other'
  };
  return mapping[category] || 'other';
}

/**
 * Map positive/negative keywords to sentiment
 * @param {Object} comment - Comment with scores
 * @returns {string} Sentiment: "positive" | "neutral" | "negative"
 */
function determineSentiment(comment) {
  const { positiveScore = 0, frictionScore = 0 } = comment;
  
  if (positiveScore > 0.4) {
    return 'positive';
  } else if (frictionScore > 0.4) {
    return 'negative';
  } else if (positiveScore > frictionScore && positiveScore > 0.2) {
    return 'positive';
  } else if (frictionScore > positiveScore && frictionScore > 0.2) {
    return 'negative';
  }
  
  return 'neutral';
}

/**
 * Extract entities/topics from comment text
 * @param {Object} comment - Comment object
 * @param {string} intent - Intent type
 * @returns {string[]} Array of entities
 */
function extractEntities(comment, intent) {
  const text = comment.text.toLowerCase();
  const entities = [];

  // Common topic keywords
  const topicMap = {
    pricing: ['price', 'cost', 'expensive', 'cheap', 'discount', 'sale', '$', 'dollar'],
    setup: ['setup', 'install', 'configure', 'initialization', 'getting started'],
    features: ['feature', 'functionality', 'capability', 'option', 'setting'],
    performance: ['speed', 'slow', 'fast', 'performance', 'lag', 'delay'],
    support: ['help', 'support', 'assistance', 'contact', 'customer service'],
    documentation: ['docs', 'documentation', 'guide', 'tutorial', 'manual'],
    bugs: ['bug', 'error', 'broken', 'issue', 'problem', 'glitch'],
    usability: ['easy', 'difficult', 'hard', 'confusing', 'intuitive', 'ux'],
    integration: ['integrate', 'api', 'connect', 'compatibility', 'works with'],
    security: ['secure', 'security', 'safe', 'privacy', 'data protection']
  };

  // Check for each topic
  Object.entries(topicMap).forEach(([topic, keywords]) => {
    if (keywords.some(keyword => text.includes(keyword))) {
      entities.push(topic);
    }
  });

  // Add intent-specific entities
  if (intent === 'purchase') {
    entities.push('buying_interest');
  } else if (intent === 'friction') {
    entities.push('user_issue');
  }

  return [...new Set(entities)]; // Remove duplicates
}

/**
 * Get the determined category for a comment
 * @param {Object} comment - Analyzed comment with scores
 * @returns {string} Category: buyer, friction, question, suggestion, positive, other
 */
function getCommentCategory(comment, analysis) {
  if (analysis.potentialBuyers.some(c => c.id === comment.id)) return 'buyer';
  if (analysis.frictionPoints.some(c => c.id === comment.id)) return 'friction';
  if (analysis.questions.some(c => c.id === comment.id)) return 'question';
  if (analysis.suggestions.some(c => c.id === comment.id)) return 'suggestion';
  if (analysis.positiveFeedback.some(c => c.id === comment.id)) return 'positive';
  return 'other';
}

/**
 * Build historical context string for prompt injection
 * @param {Object} videoContext - Video context data
 * @param {Object} channelContext - Channel context data
 * @returns {string} Context string
 */
export function buildHistoricalContextPrompt(videoContext, channelContext) {
  const parts = [];

  // Video-level context
  if (videoContext) {
    if (videoContext.inferredVideoGoal) {
      parts.push(`Video Goal: ${videoContext.inferredVideoGoal}`);
    }

    const topIntents = Object.entries(videoContext.topIntents || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([intent, count]) => `${intent} (${count})`)
      .join(', ');
    
    if (topIntents) {
      parts.push(`Common intents in this video: ${topIntents}`);
    }

    const topFriction = Object.entries(videoContext.topFrictionThemes || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([theme]) => theme)
      .join(', ');
    
    if (topFriction) {
      parts.push(`Known friction points: ${topFriction}`);
    }
  }

  // Channel-level context
  if (channelContext) {
    if (channelContext.audienceProfile) {
      parts.push(`Audience Profile: ${channelContext.audienceProfile}`);
    }

    const channelIntents = Object.entries(channelContext.recurringIntents || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([intent]) => intent)
      .join(', ');
    
    if (channelIntents) {
      parts.push(`Channel typically sees: ${channelIntents} intents`);
    }

    if (channelContext.learnedPatterns && channelContext.learnedPatterns.length > 0) {
      const recentPatterns = channelContext.learnedPatterns.slice(-3);
      parts.push(`Learned patterns: ${recentPatterns.join('; ')}`);
    }
  }

  return parts.length > 0 ? '\n\nHistorical Context:\n' + parts.join('\n') : '';
}

/**
 * Analyze comments with learning system integration
 * @param {Array} comments - Raw comments from YouTube
 * @param {string} videoId - Video ID
 * @param {string} channelId - Channel ID
 * @param {number} totalCommentCount - Total comment count from video metadata
 * @param {Object} options - Additional options
 * @returns {Object} Enhanced analysis with confidence scores
 */
export async function analyzeCommentsWithLearning(
  comments,
  videoId,
  channelId,
  totalCommentCount = null,
  options = {}
) {
  // VERSION CHECK v2.2: If you see this log, the new code is loaded
  const VERSION_TIMESTAMP = new Date().toISOString();
  console.log(`­ƒºá [Learning v2.2] Starting analysis with learning system for video: ${videoId} (loaded at ${VERSION_TIMESTAMP})`);
  console.log(`­ƒöì [Learning v2.2] STEP 1: Checking for existing signals BEFORE processing...`);
  console.log(`­ƒöì [Learning v2.2] OLD CODE DETECTED if you see "Ô£à [CommentSignals] Saved signal" without version markers`);

  try {
    // Step 1: FAST check if ANY signals exist for this video FIRST (before any processing)
    // This is a fast check that can prevent unnecessary work
    console.log(`­ƒöì [Learning v2.2] Fast check for existing signals for video: ${videoId} (${comments.length} comments)`);
    const { hasSignalsForVideo, getSignalsForVideo } = await import('./commentSignalsService');
    
    let signalsExist = false;
    try {
      signalsExist = await hasSignalsForVideo(videoId);
      console.log(`­ƒöì [Learning v2.2] Signal check result: ${signalsExist}`);
    } catch (err) {
      console.error(`ÔØî [Learning v2.2] CRITICAL: Error checking signals:`, err);
      console.warn(`ÔÜá´©Å [Learning v2.2] Proceeding with analysis, but this may cause redundant signal saving`);
      signalsExist = false;
    }
    
    let existingSignals = [];
    if (signalsExist) {
      console.log(`­ƒöì [Learning v2.2] Signals exist - fetching details...`);
      try {
        existingSignals = await getSignalsForVideo(videoId);
        console.log(`­ƒöì [Learning v2.2] Found ${existingSignals.length} existing signals for ${comments.length} comments`);
        
        // Log sample signal IDs for debugging
        if (existingSignals.length > 0) {
          console.log(`­ƒöì [Learning v2.2] Sample signal IDs:`, existingSignals.slice(0, 3).map(s => s.id));
        }
      } catch (err) {
        console.error(`ÔØî [Learning v2.2] CRITICAL: Error fetching signal details:`, err);
        console.warn(`ÔÜá´©Å [Learning v2.2] Proceeding with analysis, but this may cause redundant signal saving`);
        existingSignals = [];
      }
    } else {
      console.log(`Ôä╣´©Å [Learning v2.2] No signals found - proceeding with full analysis`);
    }
    
    // Create a map of existing signals by comment ID for fast lookup
    const existingCommentIds = new Set(existingSignals.map(s => s.id));
    const existingSignalMap = new Map(existingSignals.map(s => [s.id, s]));
    
    // Log sample comment IDs for debugging
    if (comments.length > 0) {
      console.log(`­ƒöì [Learning v2.2] Sample comment IDs from YouTube:`, comments.slice(0, 3).map(c => c.id));
    }
    
    // Filter to only process comments that don't have signals
    const commentsToProcess = comments.filter(comment => {
      const hasSignal = existingCommentIds.has(comment.id);
      return !hasSignal;
    });

    const matchedCount = comments.length - commentsToProcess.length;
    console.log(`­ƒöì [Learning v2.2] Signal matching: ${matchedCount} matched, ${commentsToProcess.length} to process out of ${comments.length} total`);
    console.log(`­ƒöì [Learning v2.2] Existing signals: ${existingSignals.length}, Comment IDs checked: ${comments.length}`);
    
    // If we found signals but they don't match, log a detailed warning
    if (existingSignals.length > 0 && commentsToProcess.length === comments.length) {
      console.error(`ÔØî [Learning v2.2] CRITICAL: Found ${existingSignals.length} signals but NONE match the ${comments.length} comments - ID MISMATCH!`);
      console.error(`ÔØî [Learning v2.2] This will cause redundant signal saving. Sample comment IDs:`, comments.slice(0, 5).map(c => c.id));
      console.error(`ÔØî [Learning v2.2] Sample signal IDs:`, Array.from(existingCommentIds).slice(0, 5));
      console.error(`ÔØî [Learning v2.2] First comment ID type:`, typeof comments[0]?.id, `First signal ID type:`, typeof Array.from(existingCommentIds)[0]);
    } else if (matchedCount > 0) {
      console.log(`Ô£à [Learning v2.2] Successfully matched ${matchedCount} existing signals`);
    }

    // If ALL comments already have signals, skip the entire learning process
    if (commentsToProcess.length === 0 && comments.length > 0) {
      console.log(`ÔÜí [Learning v2.2] All ${comments.length} comments already have signals - SKIPPING learning system processing (performance optimization)`);
      console.log(`ÔÜí [Learning v2.2] Found ${existingSignals.length} existing signals - NO NEW SIGNALS NEEDED`);
      
      // Still run basic analysis for UI, but don't save signals
      const analysis = analyzeComments(comments, totalCommentCount);
      
      // Attach confidence from existing signals
      comments.forEach(comment => {
        const existingSignal = existingSignals.find(s => s.id === comment.id);
        if (existingSignal) {
          comment.confidence = existingSignal.confidence || 0.5;
        }
      });

      // Return analysis without learning enhancements - CRITICAL: This prevents any signal saving
      return {
        ...analysis,
        learningEnabled: false,
        learningSkipped: true,
        reason: 'All signals already exist - no processing needed'
      };
    }

    // Step 2: Get historical context (only if we need to process new comments)
    let videoContext, channelContext;
    try {
      videoContext = await getVideoContext(videoId);
      channelContext = await getChannelContext(channelId);
      console.log(`­ƒôÜ [Learning] Retrieved context - Video confidence: ${videoContext.confidenceScore}, Channel patterns: ${channelContext.learnedPatterns?.length || 0}`);
    } catch (contextError) {
      console.warn(`ÔÜá´©Å [Learning] Error getting context, using defaults:`, contextError);
      // Use default empty context instead of throwing
      videoContext = {
        topIntents: {},
        topFrictionThemes: {},
        unresolvedQuestions: {},
        confidenceScore: 0
      };
      channelContext = {
        recurringIntents: {},
        historicalFriction: {},
        learnedPatterns: []
      };
    }

    // Step 3: Run standard analysis
    const analysis = analyzeComments(comments, totalCommentCount);

    // Step 4: Process only new comments (skip ones that already have signals)
    const signalsToSave = [];

    if (commentsToProcess.length > 0) {
      console.log(`­ƒôØ [Learning] Processing ${commentsToProcess.length} new comments (${existingSignals.length} already exist)`);
    }

    commentsToProcess.forEach(comment => {
      const category = getCommentCategory(comment, analysis);
      const intent = mapCategoryToIntent(category);
      const sentiment = determineSentiment(comment);
      const entities = extractEntities(comment, intent);

      // Calculate base confidence from score
      let baseConfidence = 0.5;
      switch (category) {
        case 'buyer':
          baseConfidence = comment.buyerScore || 0.5;
          break;
        case 'friction':
          baseConfidence = comment.frictionScore || 0.5;
          break;
        case 'question':
          baseConfidence = comment.questionScore || 0.5;
          break;
        case 'positive':
          baseConfidence = comment.positiveScore || 0.5;
          break;
        case 'suggestion':
          baseConfidence = comment.suggestionScore || 0.5;
          break;
        default:
          baseConfidence = 0.3;
      }

      // Calculate context-weighted confidence
      const videoFrequency = calculateVideoFrequency(intent, videoContext);
      const channelFrequency = calculateChannelFrequency(intent, channelContext);
      const entityBoost = calculateEntityMatchBoost(entities, videoContext, channelContext);
      const contradictionPenalty = calculateContradictionPenalty(intent, videoContext, channelContext);

      const adjustedConfidence = calculateConfidence({
        baseConfidence,
        videoFrequency,
        channelFrequency,
        userValidation: 0 // No user validation yet
      }) + entityBoost + contradictionPenalty;

      signalsToSave.push({
        commentId: comment.id,
        signalData: {
          sentiment,
          intent,
          confidence: Math.max(0, Math.min(1, adjustedConfidence)),
          entities,
          videoId,
          channelId
        }
      });

      // Attach confidence to comment for UI display
      comment.confidence = adjustedConfidence;
    });

    // Also attach confidence to existing comments for UI display
    comments.forEach(comment => {
      if (existingCommentIds.has(comment.id)) {
        const existingSignal = existingSignals.find(s => s.id === comment.id);
        if (existingSignal) {
          comment.confidence = existingSignal.confidence || 0.5;
        }
      }
    });

    // Step 5: Batch save signals (only if there are new ones to save)
    if (signalsToSave.length > 0) {
      try {
        console.log(`­ƒÆ¥ [Learning] Attempting to save ${signalsToSave.length} new signals (${comments.length - signalsToSave.length} already exist)`);
        await batchSaveSignals(signalsToSave);
        console.log(`Ô£à [Learning] Saved ${signalsToSave.length} new comment signals`);
      } catch (saveError) {
        console.error(`ÔØî [Learning] Error saving signals:`, saveError);
        // Don't throw - continue with analysis
      }
    } else {
      console.log(`ÔÜí [Learning] SKIPPED saving - all ${comments.length} comments already have signals (found ${existingSignals.length} existing)`);
    }

    // Step 5: Update video context with new signals
    const allSignals = await getSignalsForVideo(videoId);
    await analyzeAndUpdateVideoContext(videoId, allSignals);
    console.log(`Ô£à [Learning] Updated video context`);

    // Step 6: Check if we should run promotion to channel context
    const userVideos = options.userVideoIds || [videoId];
    const shouldPromote = await shouldRunPromotion(channelId, userVideos);
    
    if (shouldPromote && userVideos.length >= 3) {
      console.log(`­ƒöä [Learning] Running auto-promotion for channel: ${channelId}`);
      await runPromotionProcess(channelId, userVideos);
    }

    // Step 7: Build context for summary generation
    const historicalContext = buildHistoricalContextPrompt(videoContext, channelContext);

    return {
      ...analysis,
      videoContext,
      channelContext,
      historicalContext,
      learningEnabled: true,
      avgConfidence: signalsToSave.reduce((sum, s) => sum + s.signalData.confidence, 0) / signalsToSave.length
    };

  } catch (error) {
    console.error('ÔØî [Learning] Error in learning-enhanced analysis:', error);
    // Fallback to standard analysis if learning system fails
    console.warn('ÔÜá´©Å [Learning] Falling back to standard analysis');
    return {
      ...analyzeComments(comments, totalCommentCount),
      learningEnabled: false,
      learningError: error.message
    };
  }
}

/**
 * Update confidence scores based on user feedback
 * @param {string} commentId - Comment ID
 * @param {string} videoId - Video ID
 * @param {string} channelId - Channel ID
 * @param {number} feedbackScore - Feedback score (-1 to 1)
 */
export async function applyUserFeedback(commentId, videoId, channelId, feedbackScore) {
  try {
    const signal = await getSignalsForVideo(videoId).then(signals =>
      signals.find(s => s.id === commentId)
    );

    if (!signal) {
      console.warn(`ÔÜá´©Å [Learning] Signal not found for comment: ${commentId}`);
      return;
    }

    const videoContext = await getVideoContext(videoId);
    const channelContext = await getChannelContext(channelId);

    const videoFrequency = calculateVideoFrequency(signal.intent, videoContext);
    const channelFrequency = calculateChannelFrequency(signal.intent, channelContext);

    const newConfidence = calculateConfidence({
      baseConfidence: signal.confidence,
      videoFrequency,
      channelFrequency,
      userValidation: feedbackScore
    });

    await saveCommentSignal(commentId, {
      ...signal,
      confidence: newConfidence
    });

    console.log(`Ô£à [Learning] Updated confidence for comment ${commentId}: ${signal.confidence} ÔåÆ ${newConfidence}`);
  } catch (error) {
    console.error('ÔØî [Learning] Error applying user feedback:', error);
  }
}





